import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useEntryData } from '@/hooks/useEntryData';
import { useEntryNavigation } from '@/hooks/useEntryNavigation';
import { usePractice } from '@/hooks/usePractice';
import { useProfessional } from '@/hooks/useProfessional';
import { useStatistics } from '@/hooks/useStatistics';
import { usePurgeState } from '@/hooks/usePurgeState';
import { useLogout, useSelectPatient } from '@/hooks/patient.hooks';
import { useAssistedQuestionnaireLaunch } from '@/hooks/useAssistedQuestionnaireLaunch';
import { useInjectConsultation } from '@/hooks/useInjectConsultation';
import { PersistContext } from '@/context/persistContext';
import { ConfirmRestartQuestionnaireModal } from '@/components/ConfirmRestartQuestionnaireModal';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { SHARED_ROUTES } from '@/routes';
import config from '@/config';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import {
  clearPractice,
  clearProfessional,
  setMode,
  updatePractice,
  updateProfessional,
  updateInvitationId,
  updateInvitationSlug,
  updateConsultationVariant,
  updateConsultationDate,
  updateQuestionnaireId,
  setAssistedByPro,
  setPatientSegment,
  updateReferrer,
} from '@/reducers/graph';
import { RootState, Mode, ConsultationState } from '@/types/graph';
import {
  Practice,
  Professional,
  QuestionnaireVariant,
} from '@aldebaran/prisma';
import { variants } from '@aldebaran/common/qVariants';
import { GetInvitationResponse } from '@aldebaran/common/types/api';
import { ProfessionalWithPractice } from '@aldebaran/common/types';
import { UnavailableQuestionnairePage } from '@/pages/onboarding/UnavailableQuestionnairePage';
import { shouldResetAssistedLaunch } from '@/util/assistedLaunch';
import {
  hasExistingSession,
  hasGenericEntryConflict,
  hasInvitationConflict,
} from '@/util/entryConflicts';
import { resolveInvitationReferrer } from '@/util/referrer';

export interface EntryRouterProps {
  entryType: 'invitation' | 'practice' | 'professional' | 'assisted';
  /** Practice / professional slug (practice, professional entries). */
  slug?: string;
  /** Consultation id for the `assisted` (consultation-linked) launch. */
  consultationId?: string;
  queryParams?: Record<string, string>;
}

export const EntryRouter: React.FC<EntryRouterProps> = ({
  entryType,
  slug,
  consultationId,
  queryParams,
}) => {
  const dispatch = useDispatch();
  const recordStatistics = useStatistics();
  const hasProcessedRef = useRef(false);
  const { navigateToLanding, getNextRouteForProfessional, goTo } =
    useEntryNavigation();
  const purge = usePurgeState();
  const persistor = useContext(PersistContext);
  const { mutateAsync: logout } = useLogout();
  const { mutate: selectPatient, error: selectPatientError } = useSelectPatient(
    {
      required: false,
    },
  );
  const injectConsultation = useInjectConsultation({ required: false });
  const existingQuestionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );

  const existingPractice = useSelector(
    (state: RootState) => state.consultation.practice,
  );
  const existingProfessional = useSelector(
    (state: RootState) => state.consultation.professional,
  );
  const existingPatient = useSelector(
    (state: RootState) => state.consultation.patient,
  );
  const invitationId = useSelector((state: RootState) => state.invitationId);
  const referrer = useSelector((state: RootState) => state.referrer);
  const searchParams = useSelector((state: RootState) => state.searchParams);

  // Conflict rules live in util/entryConflicts.ts (with their decision-table
  // spec); this component only wires state into them.
  const existingSession = hasExistingSession({
    questionnaireId: existingQuestionnaireId,
    patientId: existingPatient?.id,
    invitationId,
  });

  // Pre-fetch conflict: detectable before API call (practice + professional)
  const hasPreFetchConflict = hasGenericEntryConflict({
    entryType,
    slug,
    existingPracticeSlug: existingPractice?.slug,
    existingProfessionalSlug: existingProfessional?.slug,
    existingSession,
  });

  // Post-fetch conflict: detectable only after API response (invitation)
  const [hasPostFetchConflict, setHasPostFetchConflict] = useState(false);

  const hasEntryConflict = hasPreFetchConflict || hasPostFetchConflict;

  // Each step is best-effort: the reload is the only way out of the spinner.
  const wipeAndReload = useCallback(async () => {
    // Prevent redux-persist from re-writing state before page unload
    persistor?.pause();
    await Promise.resolve(purge()).catch(() => undefined);
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage access blocked: nothing was persisted to clear
    }
    window.location.href = window.location.pathname + window.location.search;
  }, [purge, persistor]);

  const handleConflictRestart = useCallback(async () => {
    // Prevent entry-processing effects from re-firing after purge resets
    // Redux state (e.g. invitationId becomes null, removing the conflict
    // check, which would cause a premature navigate before the hard reload).
    hasProcessedRef.current = true;
    try {
      await logout();
    } catch {
      // Logout may fail (no active session) — cleanup must still proceed
    }
    await wipeAndReload();
  }, [logout, wipeAndReload]);

  const handleConflictContinue = useCallback(() => {
    goTo(SHARED_ROUTES.LANDING_PAGE);
  }, [goTo]);

  // --- Practice entry ---
  const practiceEntry = useEntryData(
    'practice',
    entryType === 'practice' && slug && !hasPreFetchConflict ? slug : '',
  );

  useEffect(() => {
    if (entryType !== 'practice') return;
    if (hasProcessedRef.current) return;
    if (practiceEntry.isLoading || practiceEntry.isError || !practiceEntry.data)
      return;

    const fetchedPractice = practiceEntry.data as Practice & {
      users: Professional[];
    };

    dispatch(updatePractice(fetchedPractice));
    recordStatistics(
      { section: 'PRACTICE_LANDING' },
      Boolean(fetchedPractice.demo),
    );

    // Read 'ref' directly from queryParams (synchronous) instead of Redux
    // referrer state, which is set asynchronously by useReferrer's useEffect
    // and may not be populated yet when this effect fires.
    // No INVITATION branch here: a leftover invitationId now triggers the
    // pre-fetch conflict above, so this effect can't run with one set.
    const refParam = queryParams?.['ref'] ?? referrer ?? '';
    const mode: Mode = refParam.toLowerCase().includes('qr')
      ? 'SALLE_ATTENTE'
      : 'PRACTICE_LINK';

    dispatch(setMode(mode));

    // Pro-assisted free-start via the practice (fallback): the pro picker
    // normally pre-assigns the doctor and launches the professional entry, so a
    // practice-level assisted entry with no pre-assigned doctor keeps the
    // professional-selection step. This flag drives interlocutor=OTHER and the
    // onboarding step-skip; the appointment date/time is entered on the
    // (retained) consultation-time step when needed.
    if (queryParams?.['assisted'] === 'true') {
      dispatch(setAssistedByPro(true));
    }

    recordStatistics({ section: 'START' }, Boolean(fetchedPractice.demo));

    // If there's only one professional (or the practice skips selection),
    // dispatch professional + variant immediately so NavigationProvider
    // can compute the correct flow before the redirect fires.
    const professionals = fetchedPractice.users ?? [];
    if (
      fetchedPractice.skipProfessionalSelection ||
      professionals.length === 1
    ) {
      const pro = professionals[0] as ProfessionalWithPractice | undefined;
      if (pro) {
        dispatch(
          updateProfessional({
            ...pro,
            practice: fetchedPractice,
          } as ProfessionalWithPractice),
        );
        const variant =
          (pro as Professional).defaultQuestionnaireVariant ?? 'DEFAULT';
        dispatch(updateConsultationVariant(variant));
      }
    }

    // Carry assisted=true in the URL so NavigationProvider can re-establish
    // ASSISTED_BY_PRO after a graph rebuild (setQuestionnaireConfig clears it);
    // the Redux flag alone is not durable. Matches the professional free-start
    // and the consultation-linked launch.
    goTo(
      queryParams?.['assisted'] === 'true'
        ? `${SHARED_ROUTES.LANDING_PAGE}?assisted=true`
        : SHARED_ROUTES.LANDING_PAGE,
    );

    hasProcessedRef.current = true;
  }, [
    entryType,
    practiceEntry.isLoading,
    practiceEntry.isError,
    practiceEntry.data,
    referrer,
    queryParams,
    dispatch,
    recordStatistics,
    goTo,
  ]);

  // --- Invitation entry ---
  const invitationEntry = useEntryData(
    'invitation',
    entryType === 'invitation' && slug ? slug : '',
  );

  const invitation =
    entryType === 'invitation'
      ? (invitationEntry.data as GetInvitationResponse | undefined)
      : undefined;

  const { data: invitationPractice } = usePractice(
    invitation?.consultation.practice.slug,
  );
  const { data: invitationProfessional } = useProfessional(
    invitation?.consultation.professional?.slug,
  );
  // Borne check-ins have no professional (ALDO-2759): only wait for one when
  // the consultation has one
  const isInvitationProfessionalLoaded =
    !invitation?.consultation.professional || !!invitationProfessional;

  useEffect(() => {
    if (entryType !== 'invitation') return;
    if (hasProcessedRef.current) return;
    if (!invitation) return;

    // Covers both a DIFFERENT invitation and an invitation opened over a leftover generic session
    if (
      hasInvitationConflict({
        fetchedInvitationId: invitation.id,
        existingInvitationId: invitationId,
        existingSession,
      })
    ) {
      setHasPostFetchConflict(true);
      return;
    }

    const resolvedReferrer = resolveInvitationReferrer(
      searchParams?.['ref'],
      invitation.type,
    );
    if (resolvedReferrer) dispatch(updateReferrer(resolvedReferrer));

    if (!invitationPractice || !isInvitationProfessionalLoaded) return;

    recordStatistics({ section: 'INVITATION_LANDING' });

    dispatch(setPatientSegment(invitation.segment));
    dispatch(setMode('INVITATION'));
    dispatch(updatePractice(invitationPractice));
    // Without a professional, drop any doctor left over from an earlier visit
    // so the questionnaire save doesn't attach them to this consultation
    if (invitationProfessional)
      dispatch(updateProfessional(invitationProfessional));
    else dispatch(clearProfessional());
    dispatch(updateInvitationId(invitation.id));
    // Keep the entry slug so a full reset can rebuild /invitation/:slug and
    // the restarted questionnaire stays attached to this invitation's
    // consultation (useResetQuestionnaire).
    if (slug) dispatch(updateInvitationSlug(slug));

    // Variant fallback: invitation > professional default > 'DEFAULT'. A
    // variant with a continuation (a borne check-in) runs as that
    // continuation on the patient's own device; the API switches the
    // consultation when the patient authenticates (ALDO-2759).
    const invitationVariant =
      invitation.consultation.variant ??
      invitationProfessional?.defaultQuestionnaireVariant ??
      'DEFAULT';
    const variant = (variants[invitationVariant]?.continuationVariant ??
      invitationVariant) as QuestionnaireVariant;
    dispatch(updateConsultationVariant(variant));

    recordStatistics({ section: 'ACCEPT_INVITATION' });

    const route = navigateToLanding('INVITATION', {
      practice: invitationPractice,
      noAppointment: invitation.consultation.noAppointment,
      variant,
    } as ConsultationState);
    goTo(route);

    hasProcessedRef.current = true;
  }, [
    entryType,
    slug,
    invitation,
    invitationPractice,
    invitationProfessional,
    isInvitationProfessionalLoaded,
    invitationId,
    existingSession,
    searchParams,
    dispatch,
    recordStatistics,
    navigateToLanding,
    goTo,
  ]);

  // --- Professional entry ---
  const professionalEntry = useEntryData(
    'professional',
    entryType === 'professional' && slug && !hasPreFetchConflict ? slug : '',
  );

  useEffect(() => {
    if (entryType !== 'professional') return;
    if (hasProcessedRef.current) return;
    if (
      professionalEntry.isLoading ||
      professionalEntry.isError ||
      !professionalEntry.data
    )
      return;

    const professional = professionalEntry.data as ProfessionalWithPractice;

    recordStatistics(
      { section: 'PROFESSIONAL_LANDING' },
      Boolean(professional.practice.demo),
    );

    dispatch(updatePractice(professional.practice));
    dispatch(updateProfessional(professional));
    dispatch(setMode('PROFESSIONAL_LINK'));
    recordStatistics({ section: 'PROFESSIONAL' });

    // Pro-assisted free-start for this professional. Drives interlocutor=OTHER
    // and the onboarding step-skip. The appointment date/time is entered on the
    // retained consultation-time step when staff need it (no forced
    // no-appointment mode).
    if (queryParams?.['assisted'] === 'true') {
      dispatch(setAssistedByPro(true));
    }

    // Carry assisted=true in the URL (not just the Redux flag): a graph rebuild
    // (e.g. setQuestionnaireConfig on variant load) clears ASSISTED_BY_PRO, and
    // NavigationProvider re-establishes it only from this durable URL signal —
    // mirroring the consultation-linked launch below.
    const route = getNextRouteForProfessional();
    goTo(
      queryParams?.['assisted'] === 'true' ? `${route}?assisted=true` : route,
    );

    hasProcessedRef.current = true;
  }, [
    entryType,
    professionalEntry.isLoading,
    professionalEntry.isError,
    professionalEntry.data,
    queryParams,
    dispatch,
    recordStatistics,
    getNextRouteForProfessional,
    goTo,
  ]);

  // --- Assisted entry (in-office consultation-linked questionnaire) ---
  // The pro app already set the RESTRICTED cookie via
  // POST /consultation/:id/launch-assisted-questionnaire; `consultationId` here
  // is the (non-secret) id and the GET below is authorized by that cookie.
  const launchConsultationId =
    entryType === 'assisted' ? consultationId ?? '' : '';
  const launchEntry = useAssistedQuestionnaireLaunch(launchConsultationId);
  const launchData = launchEntry.data;
  // A consultation may have no assigned professional (professionalId is
  // nullable — e.g. shared-practice / non-attributed queues), so access it
  // null-safely and fall back to the consultation's own practice slug for
  // hydration.
  const { data: launchProfessional, error: launchProfessionalError } =
    useProfessional(launchData?.professional?.slug);
  const { data: launchPractice, error: launchPracticeError } = usePractice(
    launchData?.professional?.practice?.slug ?? launchData?.practice?.slug,
  );

  // A pro-initiated launch must always start a fresh session — never resume a
  // previous patient's persisted state on a shared in-office device. The cookie
  // set by the launch call is authoritative for the *correct* patient; here we
  // only need to clear stale local (redux-persist) state, preserving the cookie
  // (so NO logout — that would clear the cookie we just received).
  const [hasLaunchConflict, setHasLaunchConflict] = useState(false);

  const handleLaunchRestart = useCallback(async () => {
    hasProcessedRef.current = true;
    await wipeAndReload();
  }, [wipeAndReload]);

  const [isOverDemoSession] = useState(() => {
    if (!isWebsiteDemoPractice(existingPractice?.slug)) return false;
    const isSameEntry =
      entryType === 'practice'
        ? slug === config.demoPracticeSlug
        : entryType === 'professional' && slug === existingProfessional?.slug;
    return !isSameEntry;
  });

  useLayoutEffect(() => {
    if (!isOverDemoSession) return;
    dispatch(clearPractice());
    if (entryType === 'assisted') handleLaunchRestart();
    else handleConflictRestart();
  }, [isOverDemoSession]);

  useEffect(() => {
    if (entryType !== 'assisted') return;
    if (hasProcessedRef.current) return;
    // Practice is always present; wait for the professional only when the
    // consultation actually has one (otherwise launchProfessional never loads).
    if (!launchData || !launchPractice) return;
    if (launchData.professional && !launchProfessional) return;

    // A pro-initiated launch must start a fresh session on a shared in-office
    // device; it should resume ONLY when re-entering the exact same launch
    // (same questionnaire AND patient). Any other prior session — a different
    // patient, a different questionnaire, or an in-progress generic free-start
    // for the same patient that has no questionnaire id yet — is stale and must
    // be reset, otherwise its answers/graph state bleed into this consultation.
    // See `shouldResetAssistedLaunch` for the full rationale + cases.
    if (
      shouldResetAssistedLaunch({
        existingQuestionnaireId,
        existingPatientId: existingPatient?.id,
        launchQuestionnaireId: launchData.questionnaireId,
        launchPatientId: launchData.patient.id,
      })
    ) {
      setHasLaunchConflict(true);
      return;
    }

    recordStatistics({ section: 'PROFESSIONAL_LANDING' });

    dispatch(updatePractice(launchPractice));
    // No assigned professional → practice-level launch; the doctor is picked on
    // landing-professional-select (kept because hasAssignedProfessional is false).
    if (launchProfessional) {
      dispatch(updateProfessional(launchProfessional));
      dispatch(setMode('PROFESSIONAL_LINK'));
    } else {
      dispatch(setMode('PRACTICE_LINK'));
    }
    dispatch(setAssistedByPro(true));

    const variant =
      launchData.variant ??
      launchProfessional?.defaultQuestionnaireVariant ??
      'DEFAULT';
    dispatch(updateConsultationVariant(variant));

    if (launchData.questionnaireId) {
      dispatch(updateQuestionnaireId(launchData.questionnaireId));
    }
    if (launchData.date) {
      // Set consultation.date directly (not only via injectConsultation, which
      // is gated on KB responses being loaded and silently no-ops otherwise).
      // skipConsultationTime reads consultation.date (gated on assistedByPro) to
      // auto-skip the consultation-time step for this linked launch — a generic
      // walk-in with no known date keeps that step so staff can enter it. We key
      // off consultation.date, not the CONSULTATION_TIME_KNOWN graph option,
      // because setQuestionnaireConfig rebuilds graph.options for non-default
      // variants and would clear that flag mid-flow.
      dispatch(updateConsultationDate(new Date(launchData.date).toISOString()));
      injectConsultation({ date: new Date(launchData.date) });
    }

    hasProcessedRef.current = true;

    // Carry `assisted=true` in the URL (not just the Redux flag). The Redux
    // ASSISTED_BY_PRO flag set above does not reliably survive until the flow
    // is finalized on the staging landing route — a store reset (PURGE on the
    // in-progress/conflict path, or a redux-persist rehydrate on a fresh
    // device) reverts graph.options before NavigationProvider computes flow[0].
    // The URL param is durable across both and is re-established into Redux by
    // NavigationProvider, mirroring the free-start flow's `?assisted=true`.
    selectPatient(launchData.patient.id, {
      onSuccess: () => goTo(`${SHARED_ROUTES.LANDING_PAGE}?assisted=true`),
    });
  }, [
    entryType,
    launchData,
    launchProfessional,
    launchPractice,
    existingQuestionnaireId,
    existingPatient,
    dispatch,
    recordStatistics,
    injectConsultation,
    selectPatient,
    goTo,
  ]);

  // --- Loading & error states ---
  const isLoading =
    (entryType === 'assisted' &&
      !hasLaunchConflict &&
      (launchEntry.isLoading ||
        (!!launchData &&
          // Practice must be hydrated; professional only when the consultation
          // has one (else launchProfessional never resolves).
          (!launchPractice ||
            (!!launchData.professional && !launchProfessional)) &&
          !hasProcessedRef.current))) ||
    (entryType === 'practice' && practiceEntry.isLoading) ||
    (entryType === 'invitation' &&
      (invitationEntry.isLoading ||
        (!!invitation &&
          (!invitationPractice || !isInvitationProfessionalLoaded) &&
          !hasEntryConflict &&
          !hasProcessedRef.current))) ||
    (entryType === 'professional' && professionalEntry.isLoading);

  const error =
    entryType === 'practice'
      ? practiceEntry.error
      : entryType === 'invitation'
      ? invitationEntry.error
      : entryType === 'professional'
      ? professionalEntry.error
      : entryType === 'assisted'
      ? launchEntry.error ??
        launchProfessionalError ??
        launchPracticeError ??
        selectPatientError
      : null;

  // Error takes precedence over loading: on a hydration-fetch failure the
  // assisted `isLoading` stays true (it waits on launchProfessional/launchPractice
  // that will never arrive), so checking error first is what lets the failure
  // surface as UnavailableQuestionnairePage instead of an infinite spinner.
  if (isOverDemoSession) {
    return <FullScreenLoader />;
  }

  if (error) {
    return <UnavailableQuestionnairePage />;
  }

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (hasEntryConflict) {
    return (
      <ConfirmRestartQuestionnaireModal
        show={true}
        onConfirm={handleConflictRestart}
        onClose={handleConflictContinue}
      />
    );
  }

  if (hasLaunchConflict) {
    // A pro-initiated launch must always start a fresh session for this
    // consultation's patient — there is no "dismiss and keep the previous
    // session" path here (unlike the generic entry conflict), since continuing
    // the prior session on a shared in-office device would mean filling it
    // under the wrong patient. The freshStart variant renders no resume button
    // and no close cross, so onClose is unreachable; it still points at the
    // restart as a safety net should the variant ever regain a dismiss path.
    return (
      <ConfirmRestartQuestionnaireModal
        variant="freshStart"
        show={true}
        onConfirm={handleLaunchRestart}
        onClose={handleLaunchRestart}
      />
    );
  }

  return <FullScreenLoader />;
};

export default EntryRouter;
